/**
 * The pilot list (F4).
 *
 * The columns are chosen for a specific question: *which pilots need someone
 * this week?* That is why KPI progress and open blockers are columns rather
 * than detail-page facts — a pilot with five KPIs and no actuals, or three
 * unresolved blockers, is the one to open, and making that visible only after
 * a click defeats the point of a list.
 *
 * @see docs/plan/IMPLEMENTATION_PLAN.md task 7.2, PRD §5.4
 */
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router';

import type { PilotSummary } from '@nexus/contracts';

import { Money } from '../../components/domain/Money';
import { PilotStatusBadge } from '../../components/domain/PilotStatusBadge';
import { Badge } from '../../components/ui/Badge';
import type { Column } from '../../components/ui/data-table-types';
import { DataTable } from '../../components/ui/DataTable';
import { ExportButton } from '../../components/ui/ExportButton';
import { PageHead } from '../../components/ui/PageHead';
import { Pagination } from '../../components/ui/Pagination';
import { formatDate, formatNumber } from '../../lib/format';
import { pilotPath, ROUTES } from '../../lib/routes';
import { useTableState } from '../../lib/use-table-state';

import { usePilots } from './api/pilots.queries';
import { PilotFilters } from './components/PilotFilters';

/** Sort fields the API allow-lists (see `PILOT_SORT_FIELDS` on the server). */
const SORT_FIELDS = [
  'title',
  'status',
  'startDate',
  'plannedEndDate',
  'createdAt',
  'updatedAt',
];

export function PilotsPage(): React.JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { state, toggleSort, setPage, setPageSize, setFilter, clearFilters } =
    useTableState(SORT_FIELDS);

  const query = useMemo(
    () => ({
      page: state.page,
      pageSize: state.pageSize,
      ...state.filters,
      ...(state.sort === null
        ? {}
        : { sort: `${state.sort.field}:${state.sort.direction}` }),
    }),
    [state],
  );

  const { data, isPending, isError } = usePilots(query);

  const columns: readonly Column<PilotSummary>[] = useMemo(
    () => [
      {
        key: 'title',
        header: t('pilot.columns.title'),
        cell: (row) => row.title,
        sortField: 'title',
        card: 'primary',
      },
      {
        key: 'startup',
        header: t('pilot.columns.startup'),
        /* The startup's name, not its id: people look for "the Terraváult
           pilot" far more often than they recall a pilot's own title. */
        cell: (row) => row.startupName,
        card: 'secondary',
      },
      {
        key: 'status',
        header: t('pilot.columns.status'),
        cell: (row) => <PilotStatusBadge status={row.status} />,
        sortField: 'status',
        card: 'secondary',
      },
      {
        key: 'host',
        header: t('pilot.columns.host'),
        /* A pilot often has neither while scoping, and "—" says "not chosen
           yet" rather than looking like a rendering failure. */
        cell: (row) => row.customerName ?? row.assetName ?? '—',
        card: 'secondary',
      },
      {
        key: 'kpis',
        header: t('pilot.columns.kpis'),
        /* Measured out of total, because that is what says whether the pilot
           has evidence yet — PRD §5.4's whole point. */
        cell: (row) =>
          row.kpiCount === 0 ? (
            <span className="text-gray-400">{t('pilot.columns.noKpis')}</span>
          ) : (
            <span className="tabular-nums">
              {t('pilot.columns.kpiRatio', {
                measured: formatNumber(row.kpiMeasuredCount),
                total: formatNumber(row.kpiCount),
              })}
            </span>
          ),
        numeric: true,
        card: 'secondary',
      },
      {
        key: 'blockers',
        header: t('pilot.columns.blockers'),
        /* Labelled, not just coloured: PRD §9.7, and a bare number does not
           say whether it is a problem. */
        cell: (row) =>
          row.openBlockerCount === 0 ? (
            <span className="text-gray-400">—</span>
          ) : (
            <Badge tone="warn">
              {t('pilot.columns.blockerCount', { count: row.openBlockerCount })}
            </Badge>
          ),
        numeric: true,
        card: 'secondary',
      },
      {
        key: 'plannedEnd',
        header: t('pilot.columns.plannedEnd'),
        cell: (row) =>
          row.plannedEndDate === null ? (
            <span className="text-gray-400">—</span>
          ) : (
            <span className="flex items-center justify-end gap-2">
              <span className="tabular-nums">{formatDate(row.plannedEndDate)}</span>
              {row.isOverdue ? <Badge tone="bad">{t('pilot.overdue')}</Badge> : null}
            </span>
          ),
        sortField: 'plannedEndDate',
        numeric: true,
        /* Dropped below 768px. A planned end and a capex figure are reference
           values you look up once inside the pilot, not the reason to open it —
           and seven unlabelled values on a phone card read as noise. */
        card: 'hidden',
      },
      {
        key: 'capex',
        header: t('pilot.columns.capex'),
        /* Abbreviated in a table, exact on the detail page (PRD §9.5). */
        cell: (row) =>
          row.capexEur === null ? (
            <span className="text-gray-400">—</span>
          ) : (
            <Money value={row.capexEur} />
          ),
        numeric: true,
        card: 'hidden',
      },
    ],
    [t],
  );

  if (isError) {
    return (
      <p role="alert" className="text-sem-bad">
        {t('errors.generic')}
      </p>
    );
  }

  return (
    <>
      <PageHead
        kicker={t('nav.group.pipeline')}
        title={t('pilot.title')}
        {...(data === undefined
          ? {}
          : { lead: t('pilot.count', { count: data.meta.total }) })}
        actions={
          <Link to={ROUTES.pilotTimeline} className="text-blue text-sm hover:underline">
            {t('pilot.timeline.link')}
          </Link>
        }
      />

      <div className="mb-s3 flex flex-wrap items-center gap-3">
        <div className="min-w-0 flex-1">
          <PilotFilters
            filters={state.filters}
            onFilterChange={setFilter}
            onClear={clearFilters}
          />
        </div>
        {/* Exports the on-screen filters and sort; hidden from Viewers (F18, §4). */}
        <ExportButton
          entity="pilots"
          query={{
            ...state.filters,
            ...(state.sort === null
              ? {}
              : { sort: `${state.sort.field}:${state.sort.direction}` }),
          }}
        />
      </div>

      <DataTable
        rows={data?.data ?? []}
        columns={columns}
        rowKey={(row) => row.id}
        caption={t('pilot.caption')}
        isLoading={isPending}
        sort={state.sort}
        onSortChange={toggleSort}
        emptyMessage={
          Object.keys(state.filters).length > 0
            ? t('pilot.emptyFiltered')
            : t('pilot.empty')
        }
        onRowSelect={(row) => {
          void navigate(pilotPath(row.id));
        }}
      />

      {data === undefined ? null : (
        <Pagination
          page={data.meta.page}
          pageSize={data.meta.pageSize}
          total={data.meta.total}
          onPageChange={setPage}
          onPageSizeChange={setPageSize}
        />
      )}
    </>
  );
}
